import type { FC, ReactNode } from 'react';
import { useMemo, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@/shared/ui';

import type { Sx } from '@/shared/types/uiTypes';

import type { SymbolKey } from '@/entities/symbol';
import { symbolPreviewOptions } from '@/features/_shared/SymbolsPreview/api/symbolPreview.options';
import { usePeriodAbsFormatter } from '@/features/_shared/user';
import CustomCheckbox from '@/shared/ui/primitives/Checkbox';

const headerTextSx: Sx = theme => ({
  fontSize: 10,
  color: theme.palette.table.infoRowText
});

const spacerCellSx: Sx = { p: 0 };

const symbolRowSx: Sx = {
  '&[data-striped="true"]': { backgroundColor: 'gx.tileLight' }
};

const tableSx: Sx = {
  '& td, & th': { px: 0 },
  '& th:first-of-type, & td:first-of-type': { pl: 2 },
  '& th:last-of-type, & td:last-of-type': { pr: 2 }
};

const checkboxCellSx: Sx = { mt: '-1px' };

export type SymbolRowMetadata = {
  Title: string;
  Currency: string;
  Units: string;
  Alias?: string;
};

export type SymbolPreviewItem = {
  symbol: SymbolKey;
  label?: string;
  date?: string;
  periodAbs?: string;
  metadata?: SymbolRowMetadata;
};

export type SymbolsPreviewSelectionState = {
  addAll: (symbols: SymbolKey[]) => void;
  removeAll: (symbols: SymbolKey[]) => void;
  isAllSelected: (symbols: SymbolKey[]) => boolean;
  isSelected: (symbol: SymbolKey) => boolean;
  toggle: (symbol: SymbolKey) => void;
  isSpotLocked: boolean;
};

const SymbolsPreview: FC<{
  items: SymbolPreviewItem[];
  enableQuery?: boolean;
  forChat?: boolean;
  hideInfo?: boolean;
  stickyHeader?: boolean;
  selectionState?: SymbolsPreviewSelectionState;
  renderTitleWrapper?: (code: string, children: ReactNode) => ReactNode;
  renderInfoCell?: (code: string) => ReactNode;
}> = ({
  items,
  enableQuery = true,
  forChat = false,
  hideInfo = false,
  stickyHeader = true,
  selectionState,
  renderTitleWrapper,
  renderInfoCell
}) => {
  const allSymbols = useMemo(
    () => items.map(item => item.symbol).filter(symbol => !!symbol.Code),
    [items]
  );

  const filteredItems = useMemo(() => items.filter(item => !!item.symbol.Code), [items]);

  const allSelected = useMemo(
    () => (selectionState ? selectionState.isAllSelected(allSymbols) : false),
    [allSymbols, selectionState]
  );
  const boxRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => boxRef.current,
    estimateSize: () => 50,
    overscan: 3,
    count: allSymbols.length,
    getItemKey: index => filteredItems[index]?.symbol?.Code ?? index
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const colSpan = 7 - (forChat ? 1 : 0) - (hideInfo ? 1 : 0);
  return (
    <Box
      ref={boxRef}
      sx={{
        my: '12px',
        mb: forChat ? 0 : undefined,
        overflowY: 'auto',
        backgroundColor: forChat ? 'gx.tileDark' : undefined
      }}
    >
      <Table
        stickyHeader={stickyHeader}
        aria-label='purchases'
        size='small'
        sx={tableSx}
      >
        <TableHead sx={{ '& th': { backgroundColor: forChat ? 'gx.tileDark' : 'inherit' } }}>
          <TableRow>
            <TableCell sx={{ pr: 2, width: '10%' }}>
              {forChat && !hideInfo && selectionState && (
                <Box
                  aria-label='select all'
                  role='button'
                  tabIndex={0}
                  sx={{
                    color: selectionState.isSpotLocked ? 'gx.textDisabled' : 'gx.highlight',
                    fontSize: 10,
                    cursor: selectionState.isSpotLocked ? 'not-allowed' : 'pointer'
                  }}
                  onClick={() => {
                    if (allSelected) {
                      selectionState.removeAll(allSymbols);
                    } else {
                      selectionState.addAll(allSymbols);
                    }
                  }}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Box>
              )}
            </TableCell>
            <TableCell sx={{ pr: 2, width: '12%' }}>
              <Typography sx={headerTextSx}>Alias</Typography>
            </TableCell>
            <TableCell sx={{ pr: 2, width: '30%' }}>
              <Typography sx={headerTextSx}>Title</Typography>
            </TableCell>
            {!forChat && (
              <TableCell sx={{ pr: 2, width: '7%' }}>
                <Typography sx={headerTextSx}>PeriodAbs</Typography>
              </TableCell>
            )}
            <TableCell
              align='right'
              sx={{ pr: 2, width: '5%' }}
            >
              <Typography sx={headerTextSx}>Currency</Typography>
            </TableCell>
            <TableCell
              align='right'
              sx={{ pr: 2, width: '7%' }}
            >
              <Typography sx={headerTextSx}>Units</Typography>
            </TableCell>
            {!hideInfo && (
              <TableCell
                align='right'
                sx={{ width: '10%', whiteSpace: 'nowrap' }}
              >
                <Typography sx={headerTextSx}>Info</Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={colSpan}
              sx={spacerCellSx}
              style={{ height: virtualRows[0]?.start ?? 0 }}
            />
          </TableRow>

          {virtualRows.map(vRow => {
            const { symbol, label, date, periodAbs, metadata } = filteredItems[vRow.index];
            return (
              <SymbolRow
                key={vRow.key}
                date={date ?? 'today'}
                enableQuery={enableQuery}
                forChat={forChat}
                hideInfo={hideInfo}
                label={label}
                measureElement={rowVirtualizer.measureElement}
                metadata={metadata}
                periodAbs={periodAbs}
                renderInfoCell={renderInfoCell}
                renderTitleWrapper={renderTitleWrapper}
                rowIndex={vRow.index}
                selectionState={selectionState}
                symbol={symbol}
              />
            );
          })}
          <TableRow>
            <TableCell
              colSpan={colSpan}
              sx={spacerCellSx}
              style={{
                height: Math.max(0, rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0))
              }}
            />
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const SymbolRow: FC<{
  enableQuery?: boolean;
  symbol: SymbolKey;
  label?: string;
  date: string;
  periodAbs?: string;
  metadata?: SymbolRowMetadata;
  forChat?: boolean;
  hideInfo?: boolean;
  rowIndex: number;
  measureElement: (element: HTMLElement | null) => void;
  selectionState?: SymbolsPreviewSelectionState;
  renderTitleWrapper?: (code: string, children: ReactNode) => ReactNode;
  renderInfoCell?: (code: string) => ReactNode;
}> = ({
  symbol,
  label,
  date,
  metadata,
  periodAbs,
  enableQuery = true,
  forChat = false,
  hideInfo = false,
  rowIndex,
  measureElement,
  selectionState,
  renderTitleWrapper,
  renderInfoCell
}) => {
  const checked = selectionState ? selectionState.isSelected(symbol) : false;
  const { data, isLoading } = useQuery({
    ...symbolPreviewOptions.bySymbol(symbol, date),
    enabled: enableQuery && !periodAbs
  });

  const { formatPeriodAbs } = usePeriodAbsFormatter();

  const row = useMemo(() => {
    let alias = metadata?.Alias ?? data?.alias;
    if (!alias || alias === '') {
      alias = symbol.Code;
    }
    return {
      alias,
      title: metadata?.Title ?? data?.title,
      periodAbs: formatPeriodAbs(periodAbs ?? data?.periodAbs),
      currency: metadata?.Currency ?? data?.currency,
      units: metadata?.Units ?? data?.units
    };
  }, [metadata, data, symbol, formatPeriodAbs, periodAbs]);

  const rowColSpan = 7 - (forChat ? 1 : 0) - (hideInfo ? 1 : 0);

  const isStriped = forChat && rowIndex % 2 === 1;

  if (isLoading) {
    return (
      <TableRow
        data-striped={isStriped}
        sx={symbolRowSx}
      >
        <TableCell colSpan={rowColSpan}>
          <Skeleton
            height='23px'
            width='100%'
          />
        </TableCell>
      </TableRow>
    );
  }

  const titleNode = (
    <Typography
      sx={hideInfo ? { textAlign: 'start' } : { textDecoration: 'underline', textAlign: 'start' }}
      variant='caption'
    >
      {row.title}
    </Typography>
  );

  return (
    <TableRow
      ref={measureElement}
      data-index={rowIndex}
      data-striped={isStriped}
      sx={symbolRowSx}
    >
      <TableCell sx={{ pr: 0.8, width: '10%' }}>
        <Box sx={checkboxCellSx}>
          {!hideInfo && selectionState && (
            <CustomCheckbox
              checked={checked}
              modeLocked={selectionState.isSpotLocked}
              onLockedClick={() => {
                selectionState.toggle(symbol);
              }}
              onChange={() => {
                selectionState.toggle(symbol);
              }}
            />
          )}
          {label && (
            <Typography
              sx={{ pl: 0.5 }}
              variant='caption'
            >
              {label}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ pr: 2, width: '12%' }}>
        <Typography variant='caption'>{row.alias}</Typography>
      </TableCell>
      <TableCell sx={{ pr: 2, width: '30%' }}>
        {hideInfo || !renderTitleWrapper ? titleNode : renderTitleWrapper(symbol.Code, titleNode)}
      </TableCell>
      {!forChat && (
        <TableCell sx={{ pr: 2, width: '7%', whiteSpace: 'nowrap' }}>
          <Typography variant='caption'>{row.periodAbs}</Typography>
        </TableCell>
      )}
      <TableCell
        align='right'
        sx={{ pr: 2, width: '7%' }}
      >
        <Typography variant='caption'>{row.currency}</Typography>
      </TableCell>
      <TableCell
        align='right'
        sx={{ pr: 2, width: '7%' }}
      >
        <Typography variant='caption'>{row.units}</Typography>
      </TableCell>
      {!hideInfo && (
        <TableCell
          align='right'
          sx={{ width: '10%' }}
        >
          <Typography variant='caption'>
            {renderInfoCell ? renderInfoCell(symbol.Code) : null}
          </Typography>
        </TableCell>
      )}
    </TableRow>
  );
};

export default SymbolsPreview;
